import { useCallback, useEffect, useRef, useState } from "react";
import { axiosInstance } from "src/services/config";
import { ServicesURL } from "@utils/utilsApi";
import {
  AsyncProcess,
  AsyncProcessListResponse,
  AsyncProcessStatusEnum,
  AsyncProcessType,
} from "src/interfaces/AsyncProcess";

interface UseAsyncProcessOptions {
  processType: AsyncProcessType;
  corporateId?: string;
  onCompleted?: (process: AsyncProcess) => void;
  onFailed?: (process: AsyncProcess) => void;
}

const POLLING_STEPS = {
  BURST: { maxElapsed: 60 * 1000, interval: 5 * 1000 }, // 0-1 min: ogni 5s
  FAST: { maxElapsed: 5 * 60 * 1000, interval: 60 * 1000 }, // 1-5 min: ogni 60s
  MEDIUM: { maxElapsed: 20 * 60 * 1000, interval: 5 * 60 * 1000 }, // 5-20 min: ogni 5 min
  SLOW: { interval: 60 * 60 * 1000 }, // 20+ min: ogni 1 ora
};

const getHideBannerKey = (
  processType: AsyncProcessType,
  corporateId?: string,
) =>
  `hide_${processType.toLowerCase()}_status_banner_${corporateId ? `${corporateId}` : "1"}`;

const readHiddenBannerUuid = (
  processType: AsyncProcessType,
  corporateId?: string,
): string | null => {
  try {
    return localStorage.getItem(getHideBannerKey(processType, corporateId));
  } catch {
    return null;
  }
};

const writeHiddenBannerUuid = (
  processType: AsyncProcessType,
  corporateId: string | undefined,
  processUuid: string,
) => {
  try {
    localStorage.setItem(
      getHideBannerKey(processType, corporateId),
      processUuid,
    );
  } catch {
    // ignore storage errors
  }
};

const getPollingInterval = (startedAt: string): number => {
  const utcDate = startedAt.endsWith("Z") ? startedAt : startedAt + "Z";
  const elapsed = Date.now() - new Date(utcDate).getTime();
  if (elapsed < POLLING_STEPS.BURST.maxElapsed)
    return POLLING_STEPS.BURST.interval;
  if (elapsed < POLLING_STEPS.FAST.maxElapsed)
    return POLLING_STEPS.FAST.interval;
  if (elapsed < POLLING_STEPS.MEDIUM.maxElapsed)
    return POLLING_STEPS.MEDIUM.interval;
  return POLLING_STEPS.SLOW.interval;
};

export const useAsyncProcess = ({
  processType,
  corporateId,
  onCompleted,
  onFailed,
}: UseAsyncProcessOptions) => {
  const [currentProcess, setCurrentProcess] = useState<AsyncProcess | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCompletedRef = useRef(onCompleted);
  const onFailedRef = useRef(onFailed);

  onCompletedRef.current = onCompleted;
  onFailedRef.current = onFailed;

  const stopPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const fetchLatestProcessRef = useRef<() => Promise<AsyncProcess | null>>(
    null!,
  );

  const fetchLatestProcess = useCallback(async () => {
    try {
      const res = await axiosInstance.get<AsyncProcessListResponse>(
        `${ServicesURL.getAsyncProcessesMe}?processType=${processType}${corporateId ? `&corporateId=${corporateId}` : ""}&page=0&size=1`,
      );
      const latest = res.data?.content?.[0] ?? null;

      const isFinalized =
        !!latest && latest.status !== AsyncProcessStatusEnum.IN_PROGRESS;
      const hiddenUuid = isFinalized
        ? readHiddenBannerUuid(processType, corporateId)
        : null;
      const alreadyHidden = isFinalized && hiddenUuid === latest!.processUuid;

      if (alreadyHidden) {
        setCurrentProcess(null);
        stopPolling();
        return null;
      }

      setCurrentProcess(latest);

      if (isFinalized) {
        stopPolling();
        if (
          latest!.status === AsyncProcessStatusEnum.COMPLETED ||
          latest!.status === AsyncProcessStatusEnum.COMPLETED_WITH_ERRORS
        ) {
          onCompletedRef.current?.(latest!);
        } else if (latest!.status === AsyncProcessStatusEnum.FAILED) {
          onFailedRef.current?.(latest!);
        }
      }

      return latest;
    } catch (error) {
      console.error("Error fetching async process:", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [processType, corporateId, stopPolling]);

  fetchLatestProcessRef.current = fetchLatestProcess;

  const scheduleNextPoll = useCallback((startedAt: string) => {
    const interval = getPollingInterval(startedAt);
    timeoutRef.current = setTimeout(async () => {
      const latest = await fetchLatestProcessRef.current();
      if (latest && latest.status === AsyncProcessStatusEnum.IN_PROGRESS) {
        scheduleNextPoll(latest.startedAt);
      }
    }, interval);
  }, []);

  const startPolling = useCallback(
    (startedAt: string) => {
      stopPolling();
      scheduleNextPoll(startedAt);
    },
    [scheduleNextPoll, stopPolling],
  );

  const startAsyncUpload = useCallback(
    async (formData: FormData) => {
      const res = await axiosInstance.post(
        `${ServicesURL.postUsersAsync}${corporateId ? `?corporate_id=${corporateId}` : ""}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      const latest = await fetchLatestProcess();
      if (latest && latest.status === AsyncProcessStatusEnum.IN_PROGRESS) {
        startPolling(latest.startedAt);
      }

      return res;
    },
    [fetchLatestProcess, startPolling, corporateId],
  );

  const downloadDiscardsCsv = useCallback(async (processUuid: string) => {
    try {
      const url = ServicesURL.getAsyncProcessDiscardsCsv.replace(
        ":processUuid",
        processUuid,
      );
      const res = await axiosInstance.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `discards_${processUuid}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading discards CSV:", error);
    }
  }, []);

  const dismissProcess = useCallback(() => {
    if (currentProcess?.processUuid) {
      writeHiddenBannerUuid(
        processType,
        corporateId,
        currentProcess.processUuid,
      );
    }
    setCurrentProcess(null);
    stopPolling();
  }, [stopPolling, currentProcess, processType, corporateId]);

  useEffect(() => {
    fetchLatestProcess().then((process) => {
      if (process && process.status === AsyncProcessStatusEnum.IN_PROGRESS) {
        startPolling(process.startedAt);
      }
    });

    return () => {
      stopPolling();
    };
  }, [fetchLatestProcess, startPolling, stopPolling]);

  const isUploadInProgress =
    currentProcess?.status === AsyncProcessStatusEnum.IN_PROGRESS;

  const progressPercentage =
    currentProcess?.progress && currentProcess.progress.totalRecords > 0
      ? Math.round(
          ((currentProcess.progress.processedRecords +
            currentProcess.progress.errorRecords) /
            currentProcess.progress.totalRecords) *
            100,
        )
      : 0;

  return {
    currentProcess,
    isLoading,
    isUploadInProgress,
    progressPercentage,
    startAsyncUpload,
    downloadDiscardsCsv,
    dismissProcess,
    refreshProcess: fetchLatestProcess,
  };
};
